import { useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { 
  User, Shield, BadgeCheck, Link2, ChevronDown, ChevronUp, 
  Mail, Phone, Calendar, Edit2, Save, X, Loader2, Check,
  Chrome, ShieldCheck, ShieldAlert,
  Briefcase, Crown, CreditCard, Building2, Plus, Trash2, Wallet,
  Lock, Key, Smartphone, Eye, EyeOff, Store, UserCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Account() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(searchParams.get('section') || 'profile');
  
  // Edit states
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const [saving, setSaving] = useState(false);

  // Bank accounts state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showAddBankModal, setShowAddBankModal] = useState(false);
  const [newBankAccount, setNewBankAccount] = useState({
    bankName: '',
    accountHolderName: '',
    branch: '',
    accountNumber: '',
    iban: '',
    swiftCode: ''
  });
  const [addingBank, setAddingBank] = useState(false);

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddCardModal, setShowAddCardModal] = useState(false);

  // Security states
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [enablingTwoFactor, setEnablingTwoFactor] = useState(false);

  // Google linking state
  const [googleLinked, setGoogleLinked] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');
  const [googleActionLoading, setGoogleActionLoading] = useState(false);

  // iter-229: Apple linking state (mirror of Google)
  const [appleLinked, setAppleLinked] = useState(false);
  const [appleEmail, setAppleEmail] = useState('');
  const [appleActionLoading, setAppleActionLoading] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('business_user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      setEditedName(userData.name || '');
      setEditedPhone(userData.phone || '');
    }
    fetchBankAccounts();
    fetchGoogleLinkStatus();
    fetchAppleLinkStatus();
    injectAppleSDK();
    setLoading(false);
  }, []);

  const fetchGoogleLinkStatus = async () => {
    try {
      const token = localStorage.getItem('business_token');
      if (!token) return;
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleLinked(!!data.google_linked);
      setGoogleEmail(data.google_email || '');
    } catch (err) {
      console.error('Failed to fetch google link status', err);
    }
  };

  // iter-229: Apple linking — status, link, unlink handlers
  const fetchAppleLinkStatus = async () => {
    try {
      const token = localStorage.getItem('business_token');
      if (!token) return;
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/apple/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppleLinked(!!data.linked);
      setAppleEmail(data.apple_email || '');
    } catch (err) {
      // 404/403 just means "not linked yet" — silent.
    }
  };

  const injectAppleSDK = () => {
    if (document.getElementById('apple-signin-sdk')) return;
    const s = document.createElement('script');
    s.id = 'apple-signin-sdk';
    s.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    s.async = true;
    document.head.appendChild(s);
  };

  const linkApple = async () => {
    if (!window.AppleID) {
      toast.error(language === 'ar' ? 'SDK Apple لم يُحمَّل بعد — أعد المحاولة' : 'Apple SDK not ready');
      return;
    }
    setAppleActionLoading(true);
    try {
      window.AppleID.auth.init({
        clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
        scope: 'name email',
        // Popup mode — no redirectURI needed. Works across every env.
        usePopup: true,
      });
      const res = await window.AppleID.auth.signIn();
      const token = localStorage.getItem('business_token');
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/apple/link`,
        { id_token: res.authorization.id_token, user: res.user },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAppleLinked(true);
      setAppleEmail(data.apple_email || '');
      toast.success(language === 'ar' ? '✅ تم ربط حساب Apple بنجاح' : '✅ Apple account linked');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.error === 'popup_closed_by_user') {
        toast.error(language === 'ar' ? 'تم إلغاء عملية الربط' : 'Link cancelled');
      } else {
        toast.error(
          (typeof detail === 'string' ? detail : null) ||
          (language === 'ar' ? 'فشل ربط حساب Apple' : 'Failed to link Apple')
        );
      }
    } finally {
      setAppleActionLoading(false);
    }
  };

  const unlinkApple = async () => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من إلغاء ربط حساب Apple؟' : 'Unlink Apple?')) return;
    setAppleActionLoading(true);
    try {
      const token = localStorage.getItem('business_token');
      await axios.post(
        `${BACKEND_URL}/api/auth/apple/unlink`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAppleLinked(false);
      setAppleEmail('');
      toast.success(language === 'ar' ? '✅ تم إلغاء ربط حساب Apple' : '✅ Apple unlinked');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || (language === 'ar' ? 'فشل إلغاء الربط' : 'Failed to unlink'));
    } finally {
      setAppleActionLoading(false);
    }
  };

  // Google link/unlink handlers — REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLinkGoogleSuccess = async (tokenResponse) => {
    setGoogleActionLoading(true);
    try {
      const userInfoResponse = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
      });

      const token = localStorage.getItem('business_token');
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/google/link`,
        {
          credential: null,
          access_token: tokenResponse.access_token,
          user_info: userInfoResponse.data
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setGoogleLinked(true);
      setGoogleEmail(data.google_email || '');
      toast.success(language === 'ar' ? '✅ تم ربط حساب Google بنجاح' : '✅ Google account linked successfully');
    } catch (error) {
      console.error('Google link error:', error);
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        const msg = language === 'ar' ? detail.message_ar : detail.message_en;
        toast.error(msg || (language === 'ar' ? 'فشل ربط حساب Google' : 'Failed to link Google'), { duration: 6000 });
      } else {
        toast.error(detail || (language === 'ar' ? 'فشل ربط حساب Google' : 'Failed to link Google'));
      }
    } finally {
      setGoogleActionLoading(false);
    }
  };

  const linkGoogle = useGoogleLogin({
    onSuccess: handleLinkGoogleSuccess,
    onError: () => toast.error(language === 'ar' ? 'تم إلغاء عملية الربط' : 'Link cancelled'),
  });

  const unlinkGoogle = async () => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من إلغاء ربط حساب Google؟' : 'Are you sure you want to unlink Google?')) return;
    setGoogleActionLoading(true);
    try {
      const token = localStorage.getItem('business_token');
      await axios.post(
        `${BACKEND_URL}/api/auth/google/unlink`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGoogleLinked(false);
      setGoogleEmail('');
      toast.success(language === 'ar' ? '✅ تم إلغاء ربط حساب Google' : '✅ Google account unlinked');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        (typeof detail === 'string' ? detail : detail?.message_ar) ||
        (language === 'ar' ? 'فشل إلغاء الربط' : 'Failed to unlink')
      );
    } finally {
      setGoogleActionLoading(false);
    }
  };

  // Handle URL section parameter
  useEffect(() => {
    const section = searchParams.get('section');
    if (section) {
      setExpandedSection(section);
    }
  }, [searchParams]);

  const fetchBankAccounts = async () => {
    try {
      const token = localStorage.getItem('business_token');
      const response = await axios.get(`${BACKEND_URL}/api/users/bank-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankAccounts(response.data || []);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? '' : section);
  };

  // Calculate profile completion percentage
  const calculateProfileCompletion = () => {
    if (!user) return 0;
    
    const fields = [
      { value: user.name, weight: 20 },
      { value: user.email, weight: 20 },
      { value: user.phone, weight: 15 },
      { value: user.emailVerified, weight: 15 },
      { value: user.phoneVerified, weight: 15 },
      { value: bankAccounts.length > 0, weight: 15 }
    ];
    
    const completed = fields.reduce((acc, field) => {
      return acc + (field.value ? field.weight : 0);
    }, 0);
    
    return Math.min(completed, 100);
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('business_token');
      const response = await axios.put(
        `${BACKEND_URL}/api/users/profile`,
        { name: editedName, phone: editedPhone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data) {
        const updatedUser = { ...user, name: editedName, phone: editedPhone };
        setUser(updatedUser);
        localStorage.setItem('business_user', JSON.stringify(updatedUser));
        toast.success(language === 'ar' ? 'تم حفظ التغييرات بنجاح' : 'Changes saved successfully');
        setIsEditing(false);
      }
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ أثناء الحفظ' : 'Error saving changes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBankAccount = async () => {
    if (!newBankAccount.bankName || !newBankAccount.accountNumber || !newBankAccount.iban) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    try {
      setAddingBank(true);
      const token = localStorage.getItem('business_token');
      await axios.post(`${BACKEND_URL}/api/users/bank-accounts`, newBankAccount, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(language === 'ar' ? 'تم إضافة الحساب البنكي بنجاح' : 'Bank account added successfully');
      setShowAddBankModal(false);
      setNewBankAccount({ bankName: '', accountHolderName: '', branch: '', accountNumber: '', iban: '', swiftCode: '' });
      fetchBankAccounts();
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ أثناء الإضافة' : 'Error adding bank account');
    } finally {
      setAddingBank(false);
    }
  };

  const handleDeleteBankAccount = async (accountId) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا الحساب؟' : 'Are you sure you want to delete this account?')) {
      return;
    }

    try {
      const token = localStorage.getItem('business_token');
      await axios.delete(`${BACKEND_URL}/api/users/bank-accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(language === 'ar' ? 'تم حذف الحساب البنكي' : 'Bank account deleted');
      fetchBankAccounts();
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ أثناء الحذف' : 'Error deleting bank account');
    }
  };

  const profileCompletion = calculateProfileCompletion();

  const sections = [
    {
      id: 'profile',
      title: language === 'ar' ? 'بيانات الحساب' : 'Account Details',
      icon: User,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'security',
      title: language === 'ar' ? 'الأمان' : 'Security',
      icon: Lock,
      color: 'from-red-500 to-rose-500'
    },
    {
      id: 'payment-methods',
      title: language === 'ar' ? 'طرق الدفع' : 'Payment Methods',
      icon: CreditCard,
      color: 'from-[#38BDF8] to-[#0EA5E9]'
    },
    {
      id: 'bank-accounts',
      title: language === 'ar' ? 'الحسابات البنكية' : 'Bank Accounts',
      icon: Building2,
      color: 'from-emerald-500 to-green-500'
    },
    {
      id: 'verification',
      title: language === 'ar' ? 'توثيق الحساب' : 'Account Verification',
      icon: Shield,
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 'account-type',
      title: language === 'ar' ? 'نوع الحساب' : 'Account Type',
      icon: BadgeCheck,
      color: 'from-[#1E9BD9] to-[#0D7AB5]'
    },
    {
      id: 'linked-accounts',
      title: language === 'ar' ? 'ربط الحسابات' : 'Linked Accounts',
      icon: Link2,
      color: 'from-amber-500 to-orange-500'
    }
  ];

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center cgm-page`}>
        <Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-6 px-4 cgm-page`}>
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className={`absolute top-20 start-10 w-[300px] h-[300px] rounded-full blur-[100px] ${isDarkMode ? 'bg-[#38BDF8]/10' : 'bg-[#38BDF8]/5'}`} />
        <div className={`absolute bottom-20 end-10 w-[250px] h-[250px] rounded-full blur-[80px] ${isDarkMode ? 'bg-[#F97316]/10' : 'bg-[#F97316]/5'}`} />
      </div>
      
      <div className="relative max-w-3xl mx-auto">
        {/* Header with Profile Completion */}
        <div className="mb-5 cgm-ai-card-elevated cgm-reveal">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="cgm-icon-glow cgm-breathe !p-2.5">
                <User className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black cgm-text-gradient-animated" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {language === 'ar' ? 'حسابي' : 'My Account'}
                </h1>
                <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'ar' ? 'إدارة بيانات حسابك وإعداداته' : 'Manage your account details and settings'}
                </p>
              </div>
            </div>
            
            {/* Profile Completion Circle */}
            <div className="flex items-center gap-3">
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 transform -rotate-90">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke={isDarkMode ? '#1E293B' : '#E2E8F0'}
                    strokeWidth="6"
                    fill="none"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="url(#gradient)"
                    strokeWidth="6"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${profileCompletion * 1.76} 176`}
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#38BDF8" />
                      <stop offset="100%" stopColor="#F97316" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-sm font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {profileCompletion}%
                  </span>
                </div>
              </div>
              <div className="hidden sm:block">
                <p className={`text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'ar' ? 'اكتمال الملف' : 'Profile Complete'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Accordion Sections */}
        <div className="space-y-3 cgm-reveal-stagger">
          {sections.map((section) => {
            const Icon = section.icon;
            const isExpanded = expandedSection === section.id;
            
            return (
              <div 
                key={section.id}
                className="cgm-ai-card !p-0 overflow-hidden"
                data-testid={`section-${section.id}`}
              >
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className={`w-full flex items-center justify-between p-4 transition-colors ${
                    isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-r ${section.color}`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {section.title}
                    </span>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  ) : (
                    <ChevronDown className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  )}
                </button>

                {/* Section Content */}
                {isExpanded && (
                  <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-[#334155]' : 'border-gray-100'}`}>
                    
                    {/* Profile Section */}
                    {section.id === 'profile' && (
                      <div className="pt-4 space-y-4">
                        {isEditing ? (
                          <>
                            <div>
                              <Label className={isDarkMode ? 'text-gray-300' : ''}>
                                {language === 'ar' ? 'الاسم' : 'Name'}
                              </Label>
                              <Input
                                value={editedName}
                                onChange={(e) => setEditedName(e.target.value)}
                                className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
                              />
                            </div>
                            <div>
                              <Label className={isDarkMode ? 'text-gray-300' : ''}>
                                {language === 'ar' ? 'رقم الهاتف' : 'Phone'}
                              </Label>
                              <Input
                                value={editedPhone}
                                onChange={(e) => setEditedPhone(e.target.value)}
                                className={isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={handleSaveProfile}
                                disabled={saving}
                                className="bg-[#1E9BD9] hover:bg-[#0369A1] text-white"
                              >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Save className="h-4 w-4 me-2" />}
                                {language === 'ar' ? 'حفظ' : 'Save'}
                              </Button>
                              <Button
                                onClick={() => {
                                  setIsEditing(false);
                                  setEditedName(user?.name || '');
                                  setEditedPhone(user?.phone || '');
                                }}
                                variant="outline"
                                className={isDarkMode ? 'border-[#334155] text-gray-300 hover:bg-white/5' : ''}
                              >
                                <X className="h-4 w-4 me-2" />
                                {language === 'ar' ? 'إلغاء' : 'Cancel'}
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="space-y-3">
                              <div className={`flex items-center gap-3 p-3 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                                <User className="h-5 w-5 text-[#1E9BD9]" />
                                <div>
                                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {language === 'ar' ? 'الاسم' : 'Name'}
                                  </p>
                                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                                    {user?.name || '-'}
                                  </p>
                                </div>
                              </div>
                              <div className={`flex items-center gap-3 p-3 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                                <Mail className="h-5 w-5 text-[#1E9BD9]" />
                                <div>
                                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                                  </p>
                                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`} dir="ltr">
                                    {user?.email || '-'}
                                  </p>
                                </div>
                                {user?.emailVerified && (
                                  <Check className="h-5 w-5 text-green-500 ms-auto" />
                                )}
                              </div>
                              <div className={`flex items-center gap-3 p-3 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                                <Phone className="h-5 w-5 text-[#1E9BD9]" />
                                <div>
                                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {language === 'ar' ? 'رقم الهاتف' : 'Phone'}
                                  </p>
                                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`} dir="ltr">
                                    {user?.phone || '-'}
                                  </p>
                                </div>
                                {user?.phoneVerified && (
                                  <Check className="h-5 w-5 text-green-500 ms-auto" />
                                )}
                              </div>
                            </div>

                            {/* iter-209: Account status — ONLY the KYC verification row */}
                            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                                 data-testid="account-status-card">
                              <p className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                {language === 'ar' ? 'حالة الحساب' : 'Account Status'}
                              </p>
                              <div className="flex items-center justify-between p-2 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <Shield className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                                  <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                    {language === 'ar' ? 'توثيق الحساب' : 'Account verified (KYC)'}
                                  </span>
                                </div>
                                {(user?.account_verified || user?.kycVerified) ? (
                                  <Check className="h-4 w-4 text-green-500" data-testid="acct-kyc-check" />
                                ) : (
                                  <X className="h-4 w-4 text-red-500" data-testid="acct-kyc-cross" />
                                )}
                              </div>
                            </div>

                            <Button
                              onClick={() => setIsEditing(true)}
                              variant="outline"
                              className={`w-full mt-2 ${isDarkMode ? 'border-[#334155] text-gray-300 hover:bg-white/5' : ''}`}
                            >
                              <Edit2 className="h-4 w-4 me-2" />
                              {language === 'ar' ? 'تعديل البيانات' : 'Edit Details'}
                            </Button>
                          </>
                        )}
                      </div>
                    )}

                    {/* Security Section */}
                    {section.id === 'security' && (
                      <div className="pt-4 space-y-4">
                        {/* Change Password */}
                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <Key className="h-5 w-5 text-[#1E9BD9]" />
                              <div>
                                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                                  {language === 'ar' ? 'كلمة المرور' : 'Password'}
                                </p>
                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {language === 'ar' ? 'قم بتغيير كلمة المرور الخاصة بك' : 'Change your password'}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowChangePassword(!showChangePassword)}
                              className={isDarkMode ? 'border-[#334155] text-gray-300 hover:bg-white/5' : ''}
                            >
                              {language === 'ar' ? 'تغيير' : 'Change'}
                            </Button>
                          </div>
                          
                          {showChangePassword && (
                            <div className="space-y-3 pt-3 border-t border-dashed border-gray-500/30">
                              <div>
                                <Label className={`text-sm ${isDarkMode ? 'text-gray-300' : ''}`}>
                                  {language === 'ar' ? 'كلمة المرور الحالية' : 'Current Password'}
                                </Label>
                                <div className="relative mt-1">
                                  <Input
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    className={`pe-10 ${isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}`}
                                    placeholder="••••••••"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    className={`absolute end-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                                  >
                                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                  </button>
                                </div>
                              </div>
                              <div>
                                <Label className={`text-sm ${isDarkMode ? 'text-gray-300' : ''}`}>
                                  {language === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}
                                </Label>
                                <div className="relative mt-1">
                                  <Input
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className={`pe-10 ${isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}`}
                                    placeholder="••••••••"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className={`absolute end-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                                  >
                                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                  </button>
                                </div>
                              </div>
                              <div>
                                <Label className={`text-sm ${isDarkMode ? 'text-gray-300' : ''}`}>
                                  {language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}
                                </Label>
                                <Input
                                  type="password"
                                  value={confirmPassword}
                                  onChange={(e) => setConfirmPassword(e.target.value)}
                                  className={`mt-1 ${isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}`}
                                  placeholder="••••••••"
                                />
                              </div>
                              <Button
                                onClick={async () => {
                                  if (!currentPassword || !newPassword || !confirmPassword) {
                                    toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
                                    return;
                                  }
                                  if (newPassword !== confirmPassword) {
                                    toast.error(language === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
                                    return;
                                  }
                                  if (newPassword.length < 6) {
                                    toast.error(language === 'ar' ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters');
                                    return;
                                  }
                                  
                                  try {
                                    setChangingPassword(true);
                                    const token = localStorage.getItem('business_token');
                                    await axios.put(
                                      `${BACKEND_URL}/api/auth/change-password`,
                                      { currentPassword, newPassword },
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    toast.success(language === 'ar' ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully');
                                    setShowChangePassword(false);
                                    setCurrentPassword('');
                                    setNewPassword('');
                                    setConfirmPassword('');
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || (language === 'ar' ? 'كلمة المرور الحالية غير صحيحة' : 'Current password is incorrect'));
                                  } finally {
                                    setChangingPassword(false);
                                  }
                                }}
                                disabled={changingPassword}
                                className="w-full bg-[#1E9BD9] hover:bg-[#0369A1] text-white"
                              >
                                {changingPassword ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Save className="h-4 w-4 me-2" />}
                                {language === 'ar' ? 'حفظ كلمة المرور الجديدة' : 'Save New Password'}
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Two-Factor Authentication */}
                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Smartphone className="h-5 w-5 text-green-500" />
                              <div>
                                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                                  {language === 'ar' ? 'التوثيق الثنائي' : 'Two-Factor Authentication'}
                                </p>
                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {language === 'ar' ? 'أضف طبقة حماية إضافية لحسابك' : 'Add an extra layer of security'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {twoFactorEnabled ? (
                                <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-500">
                                  {language === 'ar' ? 'مفعّل' : 'Enabled'}
                                </span>
                              ) : (
                                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
                                  {language === 'ar' ? 'غير مفعّل' : 'Disabled'}
                                </span>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={enablingTwoFactor}
                                onClick={() => {
                                  toast.info(language === 'ar' ? 'قريباً - هذه الميزة قيد التطوير' : 'Coming soon - This feature is under development');
                                }}
                                className={isDarkMode ? 'border-[#334155] text-gray-300 hover:bg-white/5' : ''}
                              >
                                {enablingTwoFactor ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : twoFactorEnabled ? (
                                  language === 'ar' ? 'تعطيل' : 'Disable'
                                ) : (
                                  language === 'ar' ? 'تفعيل' : 'Enable'
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Security Tips */}
                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200'}`}>
                          <div className="flex items-start gap-3">
                            <ShieldAlert className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className={`text-sm font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                                {language === 'ar' ? 'نصائح الأمان' : 'Security Tips'}
                              </p>
                              <ul className={`text-xs mt-1 space-y-1 ${isDarkMode ? 'text-amber-400/80' : 'text-amber-600'}`}>
                                <li>• {language === 'ar' ? 'استخدم كلمة مرور قوية تحتوي على أحرف وأرقام' : 'Use a strong password with letters and numbers'}</li>
                                <li>• {language === 'ar' ? 'لا تشارك كلمة المرور مع أي شخص' : 'Never share your password with anyone'}</li>
                                <li>• {language === 'ar' ? 'فعّل التوثيق الثنائي لحماية أفضل' : 'Enable 2FA for better protection'}</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Payment Methods Section */}
                    {section.id === 'payment-methods' && (
                      <div className="pt-4 space-y-4">
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' 
                            ? 'أضف بطاقتك لدفع اشتراكات المتاجر والخدمات'
                            : 'Add your card to pay for store subscriptions and services'
                          }
                        </p>
                        
                        {paymentMethods.length > 0 ? (
                          <div className="space-y-2">
                            {paymentMethods.map((method, index) => (
                              <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                                <div className="flex items-center gap-3">
                                  <CreditCard className="h-5 w-5 text-[#38BDF8]" />
                                  <span className={isDarkMode ? 'text-white' : 'text-[#0F172A]'}>
                                    **** **** **** {method.last4}
                                  </span>
                                </div>
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className={`p-6 rounded-xl text-center border-2 border-dashed ${
                            isDarkMode ? 'border-[#334155]' : 'border-gray-200'
                          }`}>
                            <CreditCard className={`h-12 w-12 mx-auto mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {language === 'ar' ? 'لم تقم بإضافة أي بطاقة بعد' : 'No payment methods added yet'}
                            </p>
                          </div>
                        )}
                        
                        <Button
                          onClick={() => setShowAddCardModal(true)}
                          className="w-full bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] hover:opacity-90 text-white"
                        >
                          <Plus className="h-4 w-4 me-2" />
                          {language === 'ar' ? 'إضافة بطاقة جديدة' : 'Add New Card'}
                        </Button>
                        
                        <p className={`text-xs text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {language === 'ar' 
                            ? 'سيتم ربط البطاقة عبر Stripe بشكل آمن'
                            : 'Cards will be securely linked via Stripe'
                          }
                        </p>
                      </div>
                    )}

                    {/* Bank Accounts Section */}
                    {section.id === 'bank-accounts' && (
                      <div className="pt-4 space-y-4">
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' 
                            ? 'أضف حسابك البنكي لاستلام أرباحك من المبيعات'
                            : 'Add your bank account to receive your sales earnings'
                          }
                        </p>
                        
                        {bankAccounts.length > 0 ? (
                          <div className="space-y-2">
                            {bankAccounts.map((account) => (
                              <div key={account._id} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                                <div className="flex items-center gap-3">
                                  <Building2 className="h-5 w-5 text-emerald-500" />
                                  <div>
                                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                                      {account.bankName}
                                      {account.branch && (
                                        <span className={`text-xs font-normal ms-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                          — {account.branch}
                                        </span>
                                      )}
                                    </p>
                                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} dir="ltr">
                                      **** {account.accountNumber?.slice(-4)}
                                    </p>
                                  </div>
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-red-500 hover:text-red-600"
                                  onClick={() => handleDeleteBankAccount(account._id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className={`p-6 rounded-xl text-center border-2 border-dashed ${
                            isDarkMode ? 'border-[#334155]' : 'border-gray-200'
                          }`}>
                            <Building2 className={`h-12 w-12 mx-auto mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {language === 'ar' ? 'لم تقم بإضافة أي حساب بنكي بعد' : 'No bank accounts added yet'}
                            </p>
                          </div>
                        )}
                        
                        <Button
                          onClick={() => setShowAddBankModal(true)}
                          className="w-full bg-gradient-to-r from-emerald-500 to-green-500 hover:opacity-90 text-white"
                        >
                          <Plus className="h-4 w-4 me-2" />
                          {language === 'ar' ? 'إضافة حساب بنكي جديد' : 'Add New Bank Account'}
                        </Button>
                      </div>
                    )}

                    {/* Verification Section */}
                    {section.id === 'verification' && (
                      <div className="pt-4 space-y-4">
                        <div className={`p-6 rounded-2xl text-center border-2 border-dashed ${
                          isDarkMode ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-emerald-300 bg-emerald-50/60'
                        }`} data-testid="kyc-empty-state">
                          <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                            isDarkMode ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20 border border-emerald-500/30' : 'bg-white shadow-md'
                          }`}>
                            <Shield className={`h-8 w-8 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                          </div>
                          <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                            {language === 'ar' ? 'وثّق حسابك للوصول لجميع المميزات' : 'Verify your account to unlock all features'}
                          </h3>
                          <p className={`text-sm mb-5 max-w-md mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {language === 'ar'
                              ? 'التوثيق (KYC) يمنحك حداً أعلى للسحب، وأولوية في الدعم، وشارة الحساب الموثّق على ملفك.'
                              : 'KYC verification unlocks higher withdrawal limits, priority support, and a verified badge on your profile.'}
                          </p>
                          <Button
                            onClick={() => toast.info(
                              language === 'ar'
                                ? 'خدمة التوثيق ستُتاح قريباً'
                                : 'Verification service is coming soon'
                            )}
                            className="bg-gradient-to-r from-emerald-500 to-green-600 hover:opacity-90 text-white shadow-lg shadow-emerald-500/20"
                            data-testid="kyc-start-btn"
                          >
                            <Shield className="h-4 w-4 me-2" />
                            {language === 'ar' ? 'ابدأ الآن بتوثيق حسابك' : 'Start verifying your account'}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Account Type Section — iter-209: two rows (merchant always ✓, agent conditional) */}
                    {section.id === 'account-type' && (
                      <div className="pt-4 space-y-3">
                        {/* Row 1: Merchant — always ✓ */}
                        <div className={`p-4 rounded-xl border flex items-center justify-between ${
                          isDarkMode ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                        }`} data-testid="account-type-merchant-row">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-[#38BDF8]/15' : 'bg-[#38BDF8]/10'}`}>
                              <Store className={`h-5 w-5 ${isDarkMode ? 'text-[#38BDF8]' : 'text-[#0EA5E9]'}`} />
                            </div>
                            <div>
                              <p className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                                {language === 'ar' ? 'حساب تاجر' : 'Merchant account'}
                              </p>
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {language === 'ar' ? 'يُفعّل تلقائياً عند إنشاء الحساب' : 'Activated automatically on account creation'}
                              </p>
                            </div>
                          </div>
                          <Check className="h-5 w-5 text-green-500" data-testid="type-merchant-check" />
                        </div>

                        {/* Row 2: Agent — ✓ if approved, else strikethrough + "apply" hint */}
                        {(() => {
                          // iter-210: detect approved agent from any of the backend fields.
                          // Backend uses `is_agent` (bool) + `role === 'agent'`; older code
                          // may still reference `agentApproved`. Consider approved if any
                          // of these are truthy AND agent isn't blocked.
                          const isApprovedAgent =
                            (user?.is_agent === true
                              || user?.role === 'agent'
                              || user?.agent_status === 'approved'
                              || user?.agentApproved === true)
                            && user?.agent_blocked !== true;
                          return (
                            <div className={`p-4 rounded-xl border flex items-center justify-between ${
                              isApprovedAgent
                                ? (isDarkMode ? 'bg-amber-500/10 border-amber-500/25' : 'bg-amber-50 border-amber-200')
                                : (isDarkMode ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200')
                            }`} data-testid="account-type-agent-row">
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${
                                  isApprovedAgent
                                    ? (isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100')
                                    : (isDarkMode ? 'bg-white/10' : 'bg-white')
                                }`}>
                                  <Crown className={`h-5 w-5 ${
                                    isApprovedAgent
                                      ? 'text-amber-500'
                                      : (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                                  }`} />
                                </div>
                                <div>
                                  <p className={`font-bold text-sm ${
                                    isApprovedAgent
                                      ? (isDarkMode ? 'text-white' : 'text-[#0F172A]')
                                      : (isDarkMode ? 'text-gray-500 line-through' : 'text-gray-400 line-through')
                                  }`}>
                                    {language === 'ar' ? 'حساب وكيل' : 'Agent account'}
                                  </p>
                                  <p className={`text-xs ${
                                    isApprovedAgent
                                      ? (isDarkMode ? 'text-amber-300' : 'text-amber-700')
                                      : (isDarkMode ? 'text-gray-500' : 'text-gray-500')
                                  }`}>
                                    {isApprovedAgent
                                      ? (language === 'ar' ? 'تم اعتماد طلبك كوكيل معتمد' : 'Your agent application is approved')
                                      : (language === 'ar' ? 'يجب التقديم لطلب وكيل ثم اعتماد الطلب' : 'Apply for agent and wait for approval')}
                                  </p>
                                </div>
                              </div>
                              {isApprovedAgent ? (
                                <Check className="h-5 w-5 text-green-500" data-testid="type-agent-check" />
                              ) : (
                                <X className="h-5 w-5 text-red-500" data-testid="type-agent-cross" />
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* Linked Accounts Section */}
                    {section.id === 'linked-accounts' && (
                      <div className="pt-4 space-y-4">
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar'
                            ? 'اربط حساب Google الخاص بك لتسجيل دخول أسرع في المرة القادمة. ملاحظة: لا يمكن إنشاء حسابات جديدة عبر Google — الربط فقط.'
                            : 'Link your Google account for faster login next time. Note: New accounts cannot be created via Google — linking only.'}
                        </p>

                        <div className="space-y-2">
                          {/* Google — real integration */}
                          <div className={`flex items-center justify-between p-4 rounded-xl border ${
                            googleLinked
                              ? (isDarkMode ? 'bg-green-500/10 border-green-500/30' : 'bg-green-50 border-green-200')
                              : (isDarkMode ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200')
                          }`} data-testid="google-link-row">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                googleLinked ? 'bg-white' : (isDarkMode ? 'bg-white/10' : 'bg-white')
                              }`}>
                                <svg className="h-5 w-5" viewBox="0 0 24 24">
                                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                              </div>
                              <div className="min-w-0">
                                <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>Google</p>
                                {googleLinked ? (
                                  <p className={`text-xs truncate ${isDarkMode ? 'text-green-400' : 'text-green-700'}`} dir="ltr" data-testid="google-linked-email">
                                    ✓ {googleEmail || (language === 'ar' ? 'مرتبط' : 'Linked')}
                                  </p>
                                ) : (
                                  <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {language === 'ar' ? 'غير مرتبط' : 'Not linked'}
                                  </p>
                                )}
                              </div>
                            </div>

                            {googleLinked ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={unlinkGoogle}
                                disabled={googleActionLoading}
                                className={`flex-shrink-0 ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-200 text-red-600 hover:bg-red-50'}`}
                                data-testid="google-unlink-btn"
                              >
                                {googleActionLoading ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  language === 'ar' ? 'إلغاء الربط' : 'Unlink'
                                )}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => linkGoogle()}
                                disabled={googleActionLoading}
                                className="flex-shrink-0 bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] text-white hover:opacity-90"
                                data-testid="google-link-btn"
                              >
                                {googleActionLoading ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <Link2 className="h-4 w-4 me-1" />
                                    {language === 'ar' ? 'ربط' : 'Link'}
                                  </>
                                )}
                              </Button>
                            )}
                          </div>

                          {/* iter-229: Apple — real integration (link-first, same rules as Google) */}
                          <div className={`flex items-center justify-between p-4 rounded-xl border ${
                            appleLinked
                              ? (isDarkMode ? 'bg-green-500/10 border-green-500/30' : 'bg-green-50 border-green-200')
                              : (isDarkMode ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200')
                          }`} data-testid="apple-link-row">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                appleLinked ? 'bg-white' : (isDarkMode ? 'bg-white/10' : 'bg-white')
                              }`}>
                                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" style={{ color: appleLinked || !isDarkMode ? '#000' : '#fff' }}>
                                  <path d="M17.05 20.28c-.98.95-2.05.86-3.08.43-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.43C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                                </svg>
                              </div>
                              <div className="min-w-0">
                                <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>Apple</p>
                                {appleLinked ? (
                                  <p className={`text-xs truncate ${isDarkMode ? 'text-green-400' : 'text-green-700'}`} dir="ltr" data-testid="apple-linked-email">
                                    ✓ {appleEmail || (language === 'ar' ? 'مرتبط' : 'Linked')}
                                  </p>
                                ) : (
                                  <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {language === 'ar' ? 'غير مرتبط' : 'Not linked'}
                                  </p>
                                )}
                              </div>
                            </div>

                            {appleLinked ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={unlinkApple}
                                disabled={appleActionLoading}
                                className={`flex-shrink-0 ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-200 text-red-600 hover:bg-red-50'}`}
                                data-testid="apple-unlink-btn"
                              >
                                {appleActionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'ar' ? 'إلغاء الربط' : 'Unlink')}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={linkApple}
                                disabled={appleActionLoading}
                                className="flex-shrink-0 bg-black text-white hover:bg-gray-800"
                                data-testid="apple-link-btn"
                              >
                                {appleActionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                                  <><Link2 className="h-4 w-4 me-1" />{language === 'ar' ? 'ربط' : 'Link'}</>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Bank Account Modal */}
      {showAddBankModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-md rounded-2xl p-6 ${
            isDarkMode ? 'bg-[#131B2C] border border-white/10' : 'bg-white shadow-xl'
          }`}>
            <h2 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              {language === 'ar' ? 'إضافة حساب بنكي' : 'Add Bank Account'}
            </h2>

            <div className="space-y-4">
              <div>
                <Label className={isDarkMode ? 'text-gray-300' : ''}>
                  {language === 'ar' ? 'اسم البنك *' : 'Bank Name *'}
                </Label>
                <Input
                  value={newBankAccount.bankName}
                  onChange={(e) => setNewBankAccount({...newBankAccount, bankName: e.target.value})}
                  placeholder={language === 'ar' ? 'مثال: بنك أبوظبي الأول' : 'e.g., First Abu Dhabi Bank'}
                  className={`mt-1 ${isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}`}
                />
              </div>
              
              <div>
                <Label className={isDarkMode ? 'text-gray-300' : ''}>
                  {language === 'ar' ? 'اسم صاحب الحساب' : 'Account Holder Name'}
                </Label>
                <Input
                  value={newBankAccount.accountHolderName}
                  onChange={(e) => setNewBankAccount({...newBankAccount, accountHolderName: e.target.value})}
                  className={`mt-1 ${isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}`}
                />
              </div>

              <div>
                <Label className={isDarkMode ? 'text-gray-300' : ''}>
                  {language === 'ar' ? 'الفرع' : 'Branch'}
                </Label>
                <Input
                  value={newBankAccount.branch}
                  onChange={(e) => setNewBankAccount({...newBankAccount, branch: e.target.value})}
                  placeholder={language === 'ar' ? 'مثال: فرع دبي — الشيخ زايد' : 'e.g., Dubai — Sheikh Zayed Branch'}
                  className={`mt-1 ${isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}`}
                  data-testid="bank-branch-input"
                />
              </div>
              
              <div>
                <Label className={isDarkMode ? 'text-gray-300' : ''}>
                  {language === 'ar' ? 'رقم الحساب *' : 'Account Number *'}
                </Label>
                <Input
                  value={newBankAccount.accountNumber}
                  onChange={(e) => setNewBankAccount({...newBankAccount, accountNumber: e.target.value})}
                  className={`mt-1 ${isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}`}
                  dir="ltr"
                />
              </div>
              
              <div>
                <Label className={isDarkMode ? 'text-gray-300' : ''}>
                  {language === 'ar' ? 'رقم IBAN *' : 'IBAN *'}
                </Label>
                <Input
                  value={newBankAccount.iban}
                  onChange={(e) => setNewBankAccount({...newBankAccount, iban: e.target.value})}
                  placeholder="AE..."
                  className={`mt-1 ${isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}`}
                  dir="ltr"
                />
              </div>
              
              <div>
                <Label className={isDarkMode ? 'text-gray-300' : ''}>
                  {language === 'ar' ? 'رمز SWIFT (اختياري)' : 'SWIFT Code (Optional)'}
                </Label>
                <Input
                  value={newBankAccount.swiftCode}
                  onChange={(e) => setNewBankAccount({...newBankAccount, swiftCode: e.target.value})}
                  className={`mt-1 ${isDarkMode ? 'bg-[#0F172A] border-[#334155] text-white' : ''}`}
                  dir="ltr"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => {
                  setShowAddBankModal(false);
                  setNewBankAccount({ bankName: '', accountHolderName: '', branch: '', accountNumber: '', iban: '', swiftCode: '' });
                }}
                variant="outline"
                className={`flex-1 ${isDarkMode ? 'border-[#334155] text-gray-300 hover:bg-white/5' : ''}`}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={handleAddBankAccount}
                disabled={addingBank}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-green-500 hover:opacity-90 text-white"
              >
                {addingBank ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'إضافة' : 'Add'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Card Modal (Placeholder for Stripe integration) */}
      {showAddCardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-md rounded-2xl p-6 ${
            isDarkMode ? 'bg-[#131B2C] border border-white/10' : 'bg-white shadow-xl'
          }`}>
            <h2 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              {language === 'ar' ? 'إضافة بطاقة دفع' : 'Add Payment Card'}
            </h2>

            <div className={`p-6 rounded-xl text-center border-2 border-dashed ${
              isDarkMode ? 'border-[#334155]' : 'border-gray-200'
            }`}>
              <CreditCard className={`h-12 w-12 mx-auto mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {language === 'ar' 
                  ? 'سيتم تفعيل هذه الخاصية قريباً عبر Stripe'
                  : 'This feature will be enabled soon via Stripe'
                }
              </p>
            </div>

            <Button
              onClick={() => setShowAddCardModal(false)}
              variant="outline"
              className={`w-full mt-4 ${isDarkMode ? 'border-[#334155] text-gray-300 hover:bg-white/5' : ''}`}
            >
              {language === 'ar' ? 'إغلاق' : 'Close'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
